import {useMemo, ReactNode} from 'react';
import {
  GlobeIcon,
  CircleQuestionMarkIcon,
  LockKeyholeIcon,
  LogOutIcon,
  UserIcon,
} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {useOpenModals} from 'context/openModals';
import {useReservation} from 'context/reservation';
import {useAuth} from './auth/useAuth';
import {goToExternalLink} from 'utils/common';
import {PATHS} from 'routes/constants';
import {closeSession, hasSession} from 'components/sessions/utils';

export enum NavLinks {
  home = 'home',
  onlineChekin = 'guest_registration',
  virtualKeys = 'virtual_keys',
  language = 'language',
  privacy = 'privacy_policy',
  faq = 'faqs',
  paymentCart = 'booking_payments',
  logout = 'logout',
  login = 'login',
  endSession = 'end_session',
  checkOut = 'check_out',
  enhanceYourStay = 'enhance_your_stay_sidebar',
}

type NavItem = {
  label: string;
  selected: boolean;
  disabled: boolean;
  icon: ReactNode;
  className?: string;
  onclick?: () => void;
  description?: string;
};

type Props = {
  onLanguageClick?: () => void;
  onAuthClick?: () => void;
  onPrivacyPolicyClick?: () => void;
  hasAuth?: boolean;
  faqsEnable?: boolean;
};
function useNavigationLinks({
  onLanguageClick,
  onAuthClick,
  onPrivacyPolicyClick,
  hasAuth,
  faqsEnable = false,
}: Props) {
  const {reservationId} = useReservation();
  const {t} = useTranslation();
  const {isLoggedIn, user} = useAuth();
  const {checkIfOpenDiscardModalAndRedirect} = useOpenModals();

  const closeSessionItem = useMemo(() => {
    return hasSession(reservationId)
      ? {
          [NavLinks.endSession]: {
            label: NavLinks.endSession,
            selected: false,
            disabled: false,
            icon: <LogOutIcon size={18} />,
            onclick: () => closeSession(reservationId),
          },
        }
      : {};
  }, [reservationId]);

  const buildAuthItem = useMemo(() => {
    const label = isLoggedIn ? NavLinks.logout : NavLinks.login;
    const authIcon = isLoggedIn ? <LogOutIcon size={18} /> : <UserIcon size={18} />;
    const description = isLoggedIn ? user?.email : undefined;

    return {
      [NavLinks.logout]: {
        label,
        selected: false,
        disabled: false,
        icon: authIcon,
        onclick: onAuthClick,
        className: 'sidebar__item-auth',
        description,
      },
    };
  }, [onAuthClick, isLoggedIn, user?.email]);

  const navigatorLinks = useMemo(() => {
    const navigatorLinks: Record<string, NavItem> = {
      [NavLinks.language]: {
        label: NavLinks.language,
        selected: false,
        disabled: false,
        icon: <GlobeIcon size={16} />,
        onclick: onLanguageClick || (() => undefined),
      },
      ...(faqsEnable && {
        [NavLinks.faq]: {
          label: NavLinks.faq,
          selected: false,
          disabled: false,
          icon: <CircleQuestionMarkIcon size={16} />,
          onclick: () => checkIfOpenDiscardModalAndRedirect(PATHS.faq),
        },
      }),
      [NavLinks.privacy]: {
        label: NavLinks.privacy,
        selected: false,
        disabled: false,
        icon: <LockKeyholeIcon size={16} />,
        onclick:
          onPrivacyPolicyClick ||
          (() => goToExternalLink(t('guest_privacy_policy_link'))),
      },
    };
    if (hasAuth) {
      return {
        ...closeSessionItem,
        ...navigatorLinks,
        ...buildAuthItem,
      };
    }
    return {...closeSessionItem, ...navigatorLinks};
  }, [
    onLanguageClick,
    onPrivacyPolicyClick,
    hasAuth,
    faqsEnable,
    checkIfOpenDiscardModalAndRedirect,
    t,
    closeSessionItem,
    buildAuthItem,
  ]);

  const navigatorLinksArray = useMemo(() => {
    return Object.values(navigatorLinks);
  }, [navigatorLinks]);

  return {navigatorLinksArray};
}

export {useNavigationLinks};
